import React, { useState } from "react";
import { useHistory } from "react-router-dom";

// API SERVICE
import AuthService from "../../store/services/auth.service";

// APP COMPONENTS
import Copyright from "../copyrightFooter";
import LoadingBox from "../indicators/LoadingBox";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CssBaseline from "@mui/material/CssBaseline";
import { grey } from "@mui/material/colors";

// ICONS
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// STYLE
import { createTheme, ThemeProvider } from "@mui/material/styles";

const theme = createTheme({
  shadows: "none",
});

function ForgotPassword() {
  const history = useHistory();

  // State
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setOpenErrorAlert(false);

    try {
      // Basic validation
      if (!email || !email.includes("@")) {
        setErrorMessage("Por favor, insira um email válido.");
        setOpenErrorAlert(true);
        setLoading(false);
        return;
      }

      // Call API to request password reset
      await AuthService.requestPasswordReset(email);

      // Always show success message (security best practice)
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      // Only show error for network/server errors, not "email not found"
      const message =
        error.response?.data?.detail ||
        error.response?.data?.non_field_errors?.[0] ||
        "Erro ao processar solicitação. Tente novamente.";
      setErrorMessage(message);
      setOpenErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    history.push("/login");
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        {/* Left Side - Background Image */}
        <Grid
          item
          xs={false}
          sm={7}
          md={8}
          sx={{
            backgroundImage: "url(/images/slapieLoginBackground.jpg)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Right Side - Form */}
        <Grid
          item
          xs={12}
          sm={5}
          md={4}
          component={Paper}
          square
          sx={{
            backgroundColor: grey["100"],
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              my: 8,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Recuperar Senha
            </Typography>

            {loading ? (
              <Box sx={{ mt: 4, width: "100%" }}>
                <LoadingBox heightMetric={"100px"} />
                <Typography align="center" variant="body2" sx={{ mt: 2 }}>
                  Processando...
                </Typography>
              </Box>
            ) : submitted ? (
              // Success Message
              <Box sx={{ mt: 3, width: "100%" }}>
                <Alert variant="filled" severity="success" sx={{ mb: 3 }}>
                  Se o email estiver cadastrado, você receberá instruções para
                  redefinir sua senha.
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Verifique sua caixa de entrada e siga as instruções no email
                  para redefinir sua senha.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleBackToLogin}
                  sx={{ mt: 2, mb: 2, py: 1.5 }}
                >
                  Voltar ao Login
                </Button>
                <Copyright sx={{ mt: 5 }} />
              </Box>
            ) : (
              // Email Form
              <Box
                component="form"
                noValidate
                onSubmit={handleSubmit}
                sx={{ mt: 3, width: "100%" }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Digite seu email cadastrado para receber instruções de
                  redefinição de senha.
                </Typography>

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  Enviar Link de Recuperação
                </Button>

                {/* Error Alert */}
                {openErrorAlert && (
                  <Alert
                    variant="filled"
                    severity="error"
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => setOpenErrorAlert(false)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                    sx={{ mb: 2 }}
                  >
                    {errorMessage}
                  </Alert>
                )}

                <Grid container justifyContent="center">
                  <Grid item>
                    <Link
                      href="/login"
                      variant="body2"
                      onClick={(e) => {
                        e.preventDefault();
                        handleBackToLogin();
                      }}
                    >
                      Voltar ao Login
                    </Link>
                  </Grid>
                </Grid>
                <Copyright sx={{ mt: 5 }} />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}

export default ForgotPassword;
